import { IItemReference, ItemReference } from "./ItemReference";
import {
  IModifierGroupReference,
  ModifierGroupReference,
} from "./ModifierGroupReference";
import { ISubgroupReference, SubgroupReference } from "./SubgroupReference";

export type GroupPricingType = "standard" | "fixed" | "discount" | "special";

/** Strategy for group-level pricing. "off" = no group pricing influence; items use their own pricing. */
export type GroupPricingStrategy = "off" | "fixed" | "size_based" | "menu_specific";

/** A size tier defined at the group level. Applied to ALL items in the group when groupPricingStrategy is "size_based". */
export interface IGroupSizePrice {
  readonly name: string;
  readonly price: number;
}

export interface IMenuGroupAvailability {
  readonly dineIn: boolean;
  readonly takeout: boolean;
  readonly delivery: boolean;
  readonly kiosk: boolean;
  readonly online: boolean;
  readonly partners: boolean;
}

/** Date-only availability window; mirrors Seasonal Availability pattern with isActive toggle. */
export interface IMenuGroupDateRange {
  readonly isActive: boolean;
  readonly startDate?: Date;
  readonly endDate?: Date;
  readonly displayMessage?: string;
}

/** Convert Firestore Timestamp, Date, or date-like value to a JS Date. */
function convertTimestamp(timestamp: any): Date | undefined {
  if (!timestamp) return undefined;
  // Check if it's a Firestore Timestamp object
  if (timestamp && typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  // Check if it's already a Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // Try to parse as string/number
  return new Date(timestamp);
}

function normalizeDateRange(
  data: IMenuGroupDateRange | undefined,
): IMenuGroupDateRange {
  if (!data) return { isActive: false };
  const hasLegacyDates =
    data.startDate != null || data.endDate != null;
  const isActive =
    (data as { isActive?: boolean }).isActive ?? hasLegacyDates;
  return {
    isActive,
    startDate: convertTimestamp(data.startDate),
    endDate: convertTimestamp(data.endDate),
    displayMessage: data.displayMessage,
  };
}

export interface IMenuGroupTimeRange {
  readonly startTime?: string;
  readonly endTime?: string;
}

export interface IMenuGroupDaysOfWeek {
  readonly sunday: boolean;
  readonly monday: boolean;
  readonly tuesday: boolean;
  readonly wednesday: boolean;
  readonly thursday: boolean;
  readonly friday: boolean;
  readonly saturday: boolean;
}

/** Single time window (HH:MM). Supports overnight (e.g. 22:00–02:00). */
export interface IMenuGroupTimeWindow {
  readonly startTime: string;
  readonly endTime: string;
  /** When false, this window is ignored (e.g. temporarily disable happy hour). Default true. */
  readonly isActive?: boolean;
}

/**
 * Canonical day order. The INDEX IS `Date.prototype.getDay()` (0 = Sunday) and
 * every per-day lookup depends on that — rotating this array silently shifts
 * every schedule by a day. Pinned by test.
 */
export const MENU_GROUP_DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

export type MenuGroupDayKey = (typeof MENU_GROUP_DAY_KEYS)[number];

/** One day's authored schedule. */
export interface IMenuGroupDaySchedule {
  /** false ⇒ unavailable all day. */
  readonly isAvailable: boolean;
  /**
   * EMPTY ⇒ available ALL DAY — the same meaning an empty window collection has
   * always had (see `menuGroupAvailability.isWithinTimeWindow`). Non-empty ⇒
   * available only inside one of these windows.
   */
  readonly windows: readonly IMenuGroupTimeWindow[];
}

/** Days-available window; mirrors Date/Seasonal pattern with isActive toggle. Mutually exclusive with dateRange and seasonalAvailability. */
export interface IMenuGroupDaysAvailable {
  readonly isActive: boolean;

  // ── DERIVED whenever `perDay` is present. See `perDay` below. ──────────────
  readonly sunday: boolean;
  readonly monday: boolean;
  readonly tuesday: boolean;
  readonly wednesday: boolean;
  readonly thursday: boolean;
  readonly friday: boolean;
  readonly saturday: boolean;
  /**
   * Time windows applied GLOBALLY across the enabled day-set (e.g. lunch
   * 11:00–14:00 and happy hour 17:00–22:00). Keyed by string id for stable
   * references. When empty or undefined, no time restriction.
   * Legacy array or startTime/endTime are migrated in normalizeDaysAvailable.
   *
   * DERIVED whenever `perDay` is present — recomputed by `projectLegacyWindows`
   * on every write. This is what readers that predate `perDay` evaluate.
   */
  readonly timeWindows?: Readonly<Record<string, IMenuGroupTimeWindow>>;
  /**
   * AUTHORED TRUTH for per-day scheduling. TOTAL — all seven keys required.
   *
   * When present it is the SOLE authority for readers that understand it, and
   * the seven day booleans plus `timeWindows` above become a CONSERVATIVE
   * PROJECTION of this object (`projectLegacyWindows`), guaranteeing
   * `legacy(day, minute) ⟹ perDay(day, minute)`. A reader that has never heard
   * of `perDay` therefore under-serves, never over-serves.
   *
   * Absent ⇒ every code path is byte-identical to before this field existed.
   * Written only when the schedule is genuinely non-uniform across days.
   */
  readonly perDay?: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>>;
  /** @deprecated Use timeWindows. Migrated to timeWindows in normalizeDaysAvailable. */
  readonly startTime?: string;
  /** @deprecated Use timeWindows. Migrated to timeWindows in normalizeDaysAvailable. */
  readonly endTime?: string;
}

/**
 * Whether `perDay` is TOTAL and well-formed enough to evaluate.
 *
 * `normalizeDaysAvailable` already refuses a partial map on the Firestore read
 * path, but it is NOT the only way a schedule reaches an evaluator:
 * `SubgroupReference.daysAvailable` and `ModifierGroupReference.daysAvailable`
 * are raw passthroughs, and `IBoardDaypart.schedule` arrives from board JSON. A
 * partial map reaching `isCurrentDayAllowed` would make the missing days
 * silently unavailable, so the evaluator checks this itself and falls back to
 * the legacy fields instead.
 */
export function isTotalPerDay(perDay: unknown): boolean {
  if (perDay == null || typeof perDay !== "object" || Array.isArray(perDay)) return false;
  const source = perDay as Record<string, unknown>;
  return MENU_GROUP_DAY_KEYS.every((key) => {
    const day = source[key];
    if (day == null || typeof day !== "object" || Array.isArray(day)) return false;
    const entry = day as { isAvailable?: unknown; windows?: unknown };
    return typeof entry.isAvailable === "boolean" && Array.isArray(entry.windows);
  });
}

/**
 * Accept a raw `perDay` only when it is TOTAL and well-formed; otherwise drop it
 * whole so every downstream path falls back to the legacy projection. A partial
 * map is refused rather than filled, because a filled default would invent
 * availability the operator never authored.
 */
function sanitizePerDay(
  raw: unknown,
): Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>> | undefined {
  // ONE definition of "total" — shared with the evaluator, which applies it to
  // the raw-passthrough paths this normalizer never sees.
  if (!isTotalPerDay(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const out: Partial<Record<MenuGroupDayKey, IMenuGroupDaySchedule>> = {};
  for (const key of MENU_GROUP_DAY_KEYS) {
    const entry = source[key] as { isAvailable?: unknown; windows?: unknown };
    out[key] = {
      isAvailable: entry.isAvailable === true,
      windows: Array.isArray(entry.windows)
        ? (entry.windows.filter(
            (w) =>
              w != null &&
              typeof w === "object" &&
              typeof (w as IMenuGroupTimeWindow).startTime === "string" &&
              typeof (w as IMenuGroupTimeWindow).endTime === "string",
          ) as IMenuGroupTimeWindow[])
        : [],
    };
  }
  return out as Record<MenuGroupDayKey, IMenuGroupDaySchedule>;
}

function normalizeDaysAvailable(
  data: IMenuGroupDaysAvailable | IMenuGroupDaysOfWeek | undefined,
): IMenuGroupDaysAvailable {
  const defaultDays = {
    sunday: true,
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
  };
  if (!data) return { isActive: false, ...defaultDays };
  const days = {
    sunday: data.sunday ?? true,
    monday: data.monday ?? true,
    tuesday: data.tuesday ?? true,
    wednesday: data.wednesday ?? true,
    thursday: data.thursday ?? true,
    friday: data.friday ?? true,
    saturday: data.saturday ?? true,
  };
  // 🔴 ALL-FALSE IS NOT A RESTRICTION — IT IS AN UNCONFIGURED GROUP.
  //
  // This inferred `isActive` from "any day is false", which is right for a
  // genuine legacy restriction (Mon–Fri on, weekend off) and catastrophically
  // wrong for the far more common shape: a group written with every day
  // defaulted to `false` and no `isActive` at all, because nobody ever opened
  // the scheduling UI.
  //
  // Those groups were synthesised to `isActive: true` with ZERO enabled days,
  // which downstream reads as "the merchant switched every day off". The
  // KitchenHub menu transformer then published every category
  // `is_deactivated: true` — an entire restaurant's menu live and 100% out of
  // stock, with no error anywhere and a green sync.
  //
  // A real restriction has SOME days on and SOME off. All-off is the empty
  // state and means no schedule, so the schedule is inactive and every day is
  // open. Measured before changing: of 37 dev menu_groups, 22 carry an explicit
  // `isActive` (untouched), 11 are all-false-no-isActive (fixed by this), and 4
  // are some-true-no-isActive (still correctly treated as restricted).
  const dayValues = Object.values(days);
  const hasLegacyRestriction =
    dayValues.some((v) => v === false) && dayValues.some((v) => v === true);
  const isActive =
    (data as IMenuGroupDaysAvailable).isActive ?? hasLegacyRestriction;
  const raw = data as IMenuGroupDaysAvailable;
  let timeWindows: Readonly<Record<string, IMenuGroupTimeWindow>> | undefined = raw.timeWindows as Readonly<Record<string, IMenuGroupTimeWindow>> | undefined;
  if (timeWindows != null && Array.isArray(timeWindows)) {
    const arr = timeWindows as unknown as IMenuGroupTimeWindow[];
    if (arr.length > 0) {
      timeWindows = Object.fromEntries(
        arr.map((w, i) => [String(i), w]),
      ) as Readonly<Record<string, IMenuGroupTimeWindow>>;
    } else {
      timeWindows = undefined;
    }
  }
  if (
    (timeWindows == null || Object.keys(timeWindows).length === 0) &&
    raw.startTime != null &&
    raw.endTime != null
  ) {
    timeWindows = {
      "0": { startTime: raw.startTime, endTime: raw.endTime, isActive: true },
    };
  }
  // NOTE: this return literal is a WHITELIST — every key not named here is
  // dropped on every read. `perDay` therefore needs an explicit spread; adding a
  // field to the interface alone is not enough to make it survive.
  const perDay = sanitizePerDay(raw.perDay);
  return {
    isActive,
    ...days,
    ...(timeWindows != null && Object.keys(timeWindows).length > 0 ? { timeWindows } : {}),
    ...(perDay != null ? { perDay } : {}),
  };
}

export interface IMenuGroupSeasonalAvailability {
  readonly isActive: boolean;
  readonly startMonth?: number;
  readonly startDay?: number;
  readonly endMonth?: number;
  readonly endDay?: number;
  readonly displayMessage?: string;
}

export interface IMenuGroup {
  readonly menuGroupId?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly parentIds: number[];
  readonly isEditing: boolean;
  readonly name: string;
  readonly posName?: string;
  readonly buttonColor?: string;
  readonly position: number;
  readonly description?: string;
  readonly items: IItemReference[];
  readonly modifierGroups: IModifierGroupReference[];
  /**
   * Optimistic revision of `modifierGroups` (design 2026-09-30 B5). Every write
   * that changes `modifierGroups` — the server's bar per-menu prices and the
   * menu editors alike — sets it to the stored value + 1, and a client write
   * that does not is refused by the Firestore rules. An editor that loaded
   * revision 3 cannot save over revision 4: that is how a menu editor open
   * across a bar price save is stopped from deleting the bar's prices.
   * Absent = 0 (never revised).
   */
  readonly modifierGroupsRev?: number;
  readonly image?: string;
  readonly isMenu: boolean;
  readonly isActive: boolean;
  readonly pricingType: GroupPricingType;
  readonly fixedPrice?: number;
  readonly discountPercentage?: number;
  readonly minRequired?: number;
  readonly maxSelections?: number;
  // ── Group-level pricing (new system — does NOT touch legacy pricingType/fixedPrice/discountPercentage) ──
  /** Group pricing strategy. "off" = items use their own pricing. Default: "off". */
  readonly groupPricingStrategy: GroupPricingStrategy;
  /** Fixed price applied to ALL items when groupPricingStrategy is "fixed". */
  readonly groupFixedPrice?: number;
  /** Size tiers when groupPricingStrategy is "size_based". POS presents these instead of item sizes. */
  readonly groupSizePrices: IGroupSizePrice[];
  readonly visibleInPOS: boolean;
  readonly visibleInKiosk: boolean;
  readonly visibleInOnlineOrdering: boolean;
  readonly visibleToPartners: boolean;
  readonly visibleInInvoices: boolean;
  readonly inheritHideModifierPrices: boolean;
  readonly availability: IMenuGroupAvailability;
  readonly dateRange: IMenuGroupDateRange;
  readonly timeRange?: IMenuGroupTimeRange;
  readonly daysOfWeek: IMenuGroupDaysOfWeek;
  readonly daysAvailable: IMenuGroupDaysAvailable;
  readonly seasonalAvailability: IMenuGroupSeasonalAvailability;
  readonly tagIds: number[];
  /** Prep stations assigned at the group level. Supersedes individual item prepStationIds for all items within this group. */
  readonly prepStationIds: number[];
  /** Course assignment at the group level. Overrides item-level courseId for all items in this group. */
  readonly courseId?: number;
  /**
   * Minutes until an item in this group is ready to hand off, inherited by
   * every item (and sub-group) that sets none of its own — the NEAREST group
   * wins. Read only when dynamic prep time is on
   * (docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md). 0 = never loads the kitchen (drinks).
   */
  readonly prepTimeMinutes?: number;
  /** Job title IDs assigned at the group level. Restricts which job roles can access/manage items in this group. */
  readonly jobTitleIds: number[];
  readonly isSpecial: boolean;
  readonly isPriority: boolean;
  /**
   * Per-parent overrides for child sub-groups.
   * Each entry defines pricing, visibility, and scheduling overrides
   * for a specific child sub-group under THIS parent group.
   * Overrides are SCOPED — they only affect items within the referenced
   * sub-group when accessed through this parent path.
   */
  readonly subgroups: ISubgroupReference[];
  readonly removedSubgroupIds: number[];
  readonly removedItemIds: number[];
  readonly removedModifierGroupIds: number[];
  readonly isArchived: boolean;
  readonly archivedAt?: Date;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export type IMenuGroupData = Partial<IMenuGroup> & { parentId?: number };

export class MenuGroup implements IMenuGroup {
  readonly menuGroupId?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly parentIds: number[];
  readonly isEditing: boolean;
  readonly name: string;
  readonly posName?: string;
  readonly buttonColor?: string;
  readonly position: number;
  readonly description?: string;
  readonly items: ItemReference[];
  readonly modifierGroups: ModifierGroupReference[];
  readonly modifierGroupsRev?: number;
  readonly image?: string;
  readonly isMenu: boolean;
  readonly isActive: boolean;
  readonly pricingType: GroupPricingType;
  readonly fixedPrice?: number;
  readonly discountPercentage?: number;
  readonly minRequired?: number;
  readonly maxSelections?: number;
  readonly groupPricingStrategy: GroupPricingStrategy;
  readonly groupFixedPrice?: number;
  readonly groupSizePrices: IGroupSizePrice[];
  readonly visibleInPOS: boolean;
  readonly visibleInKiosk: boolean;
  readonly visibleInOnlineOrdering: boolean;
  readonly visibleToPartners: boolean;
  readonly visibleInInvoices: boolean;
  readonly inheritHideModifierPrices: boolean;
  readonly availability: IMenuGroupAvailability;
  readonly dateRange: IMenuGroupDateRange;
  readonly timeRange?: IMenuGroupTimeRange;
  readonly daysOfWeek: IMenuGroupDaysOfWeek;
  readonly daysAvailable: IMenuGroupDaysAvailable;
  readonly seasonalAvailability: IMenuGroupSeasonalAvailability;
  readonly tagIds: number[];
  readonly prepStationIds: number[];
  readonly courseId?: number;
  readonly prepTimeMinutes?: number;
  readonly jobTitleIds: number[];
  readonly isSpecial: boolean;
  readonly isPriority: boolean;
  readonly subgroups: SubgroupReference[];
  readonly removedSubgroupIds: number[];
  readonly removedItemIds: number[];
  readonly removedModifierGroupIds: number[];
  readonly isArchived: boolean;
  readonly archivedAt?: Date;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;

  constructor(data: IMenuGroupData) {
    this.menuGroupId = data.menuGroupId;
    this.guid = data.guid;
    this.number = data.number;
    // Migrate legacy parentId to parentIds if present
    this.parentIds = data.parentIds ?? (data.parentId ? [data.parentId] : []);
    this.name = data.name!;
    this.posName = data.posName;
    this.buttonColor = data.buttonColor;
    this.position = data.position ?? 0;
    this.description = data.description;
    this.items = (data.items ?? []).map((i) => new ItemReference(i));
    this.modifierGroups = (data.modifierGroups ?? []).map(
      (mg) => new ModifierGroupReference(mg),
    );
    this.modifierGroupsRev =
      typeof data.modifierGroupsRev === "number" ? data.modifierGroupsRev : undefined;
    this.image = data.image;
    this.isMenu = data.isMenu ?? false;
    this.isActive = data.isActive ?? true;
    this.pricingType = data.pricingType ?? "standard";
    this.fixedPrice = data.fixedPrice;
    this.discountPercentage = data.discountPercentage;
    this.minRequired = data.minRequired;
    this.maxSelections = data.maxSelections;
    this.groupPricingStrategy = data.groupPricingStrategy ?? "off";
    this.groupFixedPrice = data.groupFixedPrice;
    this.groupSizePrices = data.groupSizePrices ?? [];
    this.visibleInPOS = data.visibleInPOS ?? true;
    this.visibleInKiosk = data.visibleInKiosk ?? true;
    this.visibleInOnlineOrdering = data.visibleInOnlineOrdering ?? true;
    this.visibleToPartners = data.visibleToPartners ?? true;
    this.visibleInInvoices = data.visibleInInvoices ?? true;
    this.inheritHideModifierPrices = data.inheritHideModifierPrices ?? true;
    this.availability = data.availability ?? {
      dineIn: true,
      takeout: true,
      delivery: true,
      kiosk: true,
      online: true,
      partners: true,
    };
    this.dateRange = normalizeDateRange(data.dateRange);
    this.timeRange = data.timeRange;
    const defaultDays = {
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
    };
    this.daysOfWeek = data.daysOfWeek ?? defaultDays;
    this.daysAvailable = normalizeDaysAvailable(data.daysAvailable);
    this.seasonalAvailability = data.seasonalAvailability ?? {
      isActive: false,
    };
    this.tagIds = data.tagIds ?? [];
    this.prepStationIds = data.prepStationIds ?? [];
    this.courseId = data.courseId;
    this.prepTimeMinutes = data.prepTimeMinutes;
    this.jobTitleIds = data.jobTitleIds ?? [];
    this.isSpecial = data.isSpecial ?? false;
    this.isPriority = data.isPriority ?? false;
    this.subgroups = (data.subgroups ?? []).map((s) => new SubgroupReference(s));
    this.removedSubgroupIds = data.removedSubgroupIds ?? [];
    this.removedItemIds = data.removedItemIds ?? [];
    this.removedModifierGroupIds = data.removedModifierGroupIds ?? [];
    this.isArchived = data.isArchived ?? false;
    this.archivedAt = data.archivedAt;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
    this.isEditing = data.isEditing ?? false;
  }

  toJSON(): IMenuGroup {
    return {
      ...this,
      items: this.items.map((i) => i.toJSON()),
      modifierGroups: this.modifierGroups.map((mg) => mg.toJSON()),
      subgroups: this.subgroups.map((s) => s.toJSON()),
    };
  }

  static fromJSON(data: Record<string, any>): MenuGroup {
    if (!data.name) throw new Error("MenuGroup.fromJSON: name is missing.");

    return new MenuGroup({
      ...data,
      dateRange: normalizeDateRange(data.dateRange),
      parentIds: data.parentIds,
      parentId: data.parentId, // Pass through for migration logic in constructor
      subgroups: Array.isArray(data.subgroups)
        ? data.subgroups.map((sg) => SubgroupReference.fromJSON(sg))
        : [],
      items: Array.isArray(data.items)
        ? data.items.map((item) => ItemReference.fromJSON(item))
        : [],
      modifierGroups: Array.isArray(data.modifierGroups)
        ? data.modifierGroups.map((group) =>
            ModifierGroupReference.fromJSON(group),
          )
        : [],
      isArchived: (data.isArchived as boolean | undefined) ?? false,
      archivedAt: convertTimestamp(data.archivedAt),
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    } as IMenuGroupData);
  }
}
